import { supportTicketService, SupportTicket } from "./SupportTicketService";
import {
  supportFeedbackService,
  FeedbackStats,
} from "./SupportFeedbackService";
import { logger } from "../lib/logger";

export interface AnalyticsMetrics {
  totalTickets: number;
  openTickets: number;
  resolvedTickets: number;
  overdueTickets: number;
  averageResponseTime: number; // en heures
  slaComplianceRate: number; // pourcentage
  deflectionRate: number; // pourcentage
  autoResolutionRate: number; // pourcentage
  satisfactionScore: number; // 1-5
  feedbackCount: number; // nombre de feedbacks réels collectés
  feedbackDistribution: Array<{ rating: number; count: number }>; // distribution 1-5
  ticketRate: number; // tickets par 1000 utilisateurs
  totalUsers: number; // nombre total d'utilisateurs actifs
  // Tickets Fantômes
  ghostResolutions: number; // nombre de résolutions sans ticket
  ghostResolutionRate: number; // pourcentage de déflexion fantôme
  // Répartition par type
  ticketTypeDistribution: Array<{
    type: string;
    count: number;
    percentage: number;
  }>;
  // Top problèmes
  topProblems: Array<{
    subject: string;
    count: number;
    severity: string;
    ghostCount?: number; // nombre de résolutions fantômes pour ce problème
  }>;
  // Stats quotidiennes
  dailyStats: Array<{
    date: string;
    tickets: number;
    resolved: number;
    overdue: number;
    ghostResolutions: number; // résolutions fantômes du jour
  }>;
}

export interface AnalyticsFilters {
  startDate: Date;
  endDate: Date;
  status?: string;
  severity?: string;
}

export class SupportAnalyticsService {
  private static instance: SupportAnalyticsService;

  public static getInstance(): SupportAnalyticsService {
    if (!SupportAnalyticsService.instance) {
      SupportAnalyticsService.instance = new SupportAnalyticsService();
    }
    return SupportAnalyticsService.instance;
  }

  /**
   * Calcule les métriques analytics complètes
   */
  async calculateMetrics(
    filters?: AnalyticsFilters,
  ): Promise<AnalyticsMetrics> {
    try {
      const tickets = await supportTicketService.listTickets({});

      // Filtrer par dates si spécifié
      const filteredTickets = this.filterTickets(tickets, filters);

      // Métriques de base
      const totalTickets = filteredTickets.length;
      const openTickets = filteredTickets.filter(
        (t) => t.status === "open",
      ).length;
      const resolvedTickets = filteredTickets.filter(
        (t) => t.status === "resolved",
      ).length;

      // Calcul SLA
      const overdueTickets = this.calculateOverdueTickets(filteredTickets);
      const slaComplianceRate =
        totalTickets > 0
          ? ((totalTickets - overdueTickets) / totalTickets) * 100
          : 100;

      // Temps de réponse moyen
      const averageResponseTime =
        await this.calculateAverageResponseTime(filteredTickets);

      // Taux de déflexion (simulé - à implémenter avec vraies données)
      const deflectionRate = this.calculateDeflectionRate(filteredTickets);

      // Taux d'auto-résolution (simulé - à implémenter avec vraies données)
      const autoResolutionRate =
        this.calculateAutoResolutionRate(filteredTickets);

      // Score de satisfaction — vrais feedbacks, fallback simulé si aucun
      const feedbackStats = await supportFeedbackService.getFeedbackStats(
        filters?.startDate ?? new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        filters?.endDate ?? new Date(),
      );
      const satisfactionScore =
        feedbackStats.totalFeedbacks > 0
          ? feedbackStats.averageRating
          : this.calculateSimulatedSatisfactionScore(filteredTickets);

      // Top problèmes
      const topProblems = this.getTopProblems(filteredTickets);

      // Répartition par type
      const ticketTypeDistribution =
        this.getTicketTypeDistribution(filteredTickets);

      // Stats quotidiennes
      const dailyStats = this.getDailyStats(filteredTickets);

      // Calcul Ticket Rate et utilisateurs actifs
      const { ticketRate, totalUsers } =
        await this.calculateTicketRate(filteredTickets);

      // Calcul Tickets Fantômes
      const { ghostResolutions, ghostResolutionRate } =
        this.calculateGhostResolutions(filteredTickets);

      return {
        totalTickets,
        openTickets,
        resolvedTickets,
        overdueTickets,
        averageResponseTime,
        slaComplianceRate,
        deflectionRate,
        autoResolutionRate,
        satisfactionScore,
        feedbackCount: feedbackStats.totalFeedbacks,
        feedbackDistribution: feedbackStats.distribution,
        ticketRate,
        totalUsers,
        ghostResolutions,
        ghostResolutionRate,
        ticketTypeDistribution,
        topProblems,
        dailyStats,
      };
    } catch (error) {
      logger.error("Error calculating analytics metrics", "analytics", {
        error,
      });
      throw error;
    }
  }

  /**
   * Exporte les métriques en CSV
   */
  async exportToCSV(filters?: AnalyticsFilters): Promise<string> {
    try {
      const metrics = await this.calculateMetrics(filters);

      // En-têtes CSV
      const headers = [
        "Date",
        "Total Tickets",
        "Tickets Ouverts",
        "Tickets Résolus",
        "Tickets en Retard",
        "Temps Réponse Moyen (h)",
        "Taux Conformité SLA (%)",
        "Taux Déflexion (%)",
        "Taux Auto-Résolution (%)",
        "Score Satisfaction",
        "Ticket Rate (/1000)",
        "Résolutions Fantômes",
      ];

      // Lignes de données
      const rows = metrics.dailyStats.map((stat) => [
        stat.date,
        stat.tickets.toString(),
        metrics.openTickets.toString(),
        metrics.resolvedTickets.toString(),
        metrics.overdueTickets.toString(),
        metrics.averageResponseTime.toString(),
        metrics.slaComplianceRate.toFixed(1),
        metrics.deflectionRate.toFixed(1),
        metrics.autoResolutionRate.toFixed(1),
        metrics.satisfactionScore.toString(),
        metrics.ticketRate.toString(),
        stat.ghostResolutions.toString(),
      ]);

      // Construire CSV
      const csvContent = [
        headers.join(","),
        ...rows.map((row) => row.join(",")),
      ].join("\n");

      return csvContent;
    } catch (error) {
      logger.error("Error exporting to CSV", "analytics", { error });
      throw error;
    }
  }

  /**
   * Filtre les tickets selon les critères
   */
  private filterTickets(
    tickets: SupportTicket[],
    filters?: AnalyticsFilters,
  ): SupportTicket[] {
    if (!filters) return tickets;

    return tickets.filter((ticket) => {
      const ticketDate = new Date(ticket.created_at);

      // Filtre par dates
      if (filters.startDate && ticketDate < filters.startDate) return false;
      if (filters.endDate && ticketDate > filters.endDate) return false;

      // Filtre par statut
      if (filters.status && ticket.status !== filters.status) return false;

      // Filtre par sévérité
      if (filters.severity && ticket.severity !== filters.severity)
        return false;

      return true;
    });
  }

  /**
   * Calcule le nombre de tickets en retard
   */
  private calculateOverdueTickets(tickets: SupportTicket[]): number {
    return tickets.filter((ticket) => this.isTicketOverdue(ticket)).length;
  }

  /**
   * Calcule le temps de réponse moyen
   */
  private async calculateAverageResponseTime(
    tickets: SupportTicket[],
  ): Promise<number> {
    try {
      // Calculer le temps de réponse réel basé sur les tickets résolus
      const resolvedTickets = tickets.filter((t) => t.status === "resolved");

      if (resolvedTickets.length === 0) return 2.5; // Valeur par défaut si aucune résolution

      const responseTimes = resolvedTickets.map((ticket) => {
        const createdAt = new Date(ticket.created_at);
        const resolvedAt = ticket.updated_at
          ? new Date(ticket.updated_at)
          : new Date();
        return (resolvedAt.getTime() - createdAt.getTime()) / (1000 * 60 * 60); // en heures
      });

      // Moyenne des temps de réponse
      const averageTime =
        responseTimes.reduce((sum, time) => sum + time, 0) /
        responseTimes.length;

      return averageTime;
    } catch (error) {
      logger.error("Error calculating average response time", "analytics", {
        error,
      });
      return 2.5; // Valeur par défaut en cas d'erreur
    }
  }

  /**
   * Calcule le taux de déflexion
   */
  private calculateDeflectionRate(tickets: SupportTicket[]): number {
    try {
      // Taux de déflexion = (tickets sans ticket) / (tickets totaux + tickets sans ticket)

      // Pour l'instant, simulation basé sur le ratio de résolutions fantômes
      const ghostResolutions = Math.floor(tickets.length * 0.25); // Simulation 25% des tickets sont résolus sans ticket

      if (tickets.length === 0) return 0; // Pas de tickets = 0% déflexion

      const deflectionRate =
        (ghostResolutions / (tickets.length + ghostResolutions)) * 100;

      return deflectionRate;
    } catch (error) {
      logger.error("Error calculating deflection rate", "analytics", { error });
      return 0; // Erreur = 0% déflexion
    }
  }

  /**
   * Calcule le taux d'auto-résolution
   */
  private calculateAutoResolutionRate(tickets: SupportTicket[]): number {
    try {
      // Taux d'auto-résolution basé sur les tickets marqués comme auto-résolus
      const autoResolved = tickets.filter(
        (t) => t.metadata?.auto_resolved,
      ).length;

      if (tickets.length === 0) return 0; // Pas de tickets = 0% auto-résolution

      const autoResolutionRate = (autoResolved / tickets.length) * 100;

      return autoResolutionRate;
    } catch (error) {
      logger.error("Error calculating auto resolution rate", "analytics", {
        error,
      });
      return 0; // Erreur = 0% auto-résolution
    }
  }

  /**
   * Calcule le score de satisfaction simulé (fallback si pas de vrais feedbacks)
   */
  private calculateSimulatedSatisfactionScore(
    tickets: SupportTicket[],
  ): number {
    // Si pas de tickets, retourne une valeur neutre
    if (tickets.length === 0) return 3.0; // Neutre par défaut

    // Score basé sur les tickets résolus et leur sévérité
    const resolvedTickets = tickets.filter((t) => t.status === "resolved");

    if (resolvedTickets.length === 0) return 3.0; // Pas de résolution = neutre

    const score = resolvedTickets.reduce((sum, ticket) => {
      // Score plus élevé pour les tickets résolus rapidement et les sévérités basses
      const severityScore = {
        blocker: 4.5, // Très critique : 4.5/5
        critical: 4.0, // Critique : 4.0/5
        normal: 3.5, // Normal : 3.5/5
        low: 3.0, // Faible : 3.0/5
      };

      // Bonus pour résolution rapide (moins de 24h)
      const createdAt = new Date(ticket.created_at);
      const resolvedAt = ticket.updated_at
        ? new Date(ticket.updated_at)
        : new Date();
      const resolutionTimeHours =
        (resolvedAt.getTime() - createdAt.getTime()) / (1000 * 60 * 60);

      let timeBonus = 0;
      if (resolutionTimeHours < 24) timeBonus = 0.5; // Résolution rapide = +0.5
      if (resolutionTimeHours < 12) timeBonus = 1.0; // Très rapide = +1.0
      if (resolutionTimeHours < 6) timeBonus = 1.5; // Ultra rapide = +1.5

      return (
        sum +
        severityScore[ticket.severity as keyof typeof severityScore] +
        timeBonus
      );
    }, 0);

    // Normaliser le score entre 1 et 5
    const averageScore = score / resolvedTickets.length;

    return Math.min(5.0, Math.max(1.0, averageScore));
  }

  /**
   * Extrait les top problèmes avec répartition par type
   */
  private getTopProblems(tickets: SupportTicket[]): Array<{
    subject: string;
    count: number;
    severity: string;
    ghostCount?: number;
  }> {
    const problemCounts = new Map<
      string,
      { count: number; severity: string; type: string }
    >();

    tickets.forEach((ticket) => {
      const subject = ticket.subject || "Sans sujet";
      const existing = problemCounts.get(subject) || {
        count: 0,
        severity: ticket.severity || "medium",
        type: "unknown",
      };
      problemCounts.set(subject, {
        count: existing.count + 1,
        severity: ticket.severity || "medium",
        type: this.getTicketType(ticket),
      });
    });

    return Array.from(problemCounts.entries())
      .map(([subject, data]) => ({
        subject,
        count: data.count,
        severity: data.severity,
        ghostCount: Math.floor(data.count * 0.3), // Simulé: 30% de résolutions fantômes
        type: data.type,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }

  /**
   * Calcule la répartition par type de ticket
   */
  private getTicketTypeDistribution(
    tickets: SupportTicket[],
  ): Array<{ type: string; count: number; percentage: number }> {
    const typeCounts = new Map<string, number>();

    tickets.forEach((ticket) => {
      const type = this.getTicketType(ticket);
      typeCounts.set(type, (typeCounts.get(type) || 0) + 1);
    });

    const total = tickets.length;

    return Array.from(typeCounts.entries())
      .map(([type, count]) => ({
        type,
        count,
        percentage: total > 0 ? Math.round((count / total) * 100) : 0,
      }))
      .sort((a, b) => b.count - a.count);
  }

  /**
   * Détecte le type de ticket à partir de son sujet
   */
  private getTicketType(ticket: SupportTicket): string {
    // Basé sur le sujet pour déterminer le type
    const subject = (ticket.subject || "").toLowerCase();

    // Détection par mots-clés
    if (
      subject.includes("bug") ||
      subject.includes("erreur") ||
      subject.includes("plantir") ||
      subject.includes("problème")
    ) {
      return "bug";
    }

    if (
      subject.includes("question") ||
      subject.includes("aide") ||
      subject.includes("comment")
    ) {
      return "question";
    }

    if (
      subject.includes("feature") ||
      subject.includes("ajout") ||
      subject.includes("amélioration")
    ) {
      return "feature";
    }

    if (
      subject.includes("feedback") ||
      subject.includes("avis") ||
      subject.includes("suggestion")
    ) {
      return "feedback";
    }

    // Par défaut
    return "other";
  }

  /**
   * Génère les stats quotidiennes
   */
  private getDailyStats(tickets: SupportTicket[]): Array<{
    date: string;
    tickets: number;
    resolved: number;
    overdue: number;
    ghostResolutions: number;
  }> {
    // Initialiser les stats pour les 30 derniers jours
    const dailyStats = new Map<
      string,
      {
        tickets: number;
        resolved: number;
        overdue: number;
        ghostResolutions: number;
      }
    >();
    for (let i = 0; i < 30; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split("T")[0];
      dailyStats.set(dateStr, {
        tickets: 0,
        resolved: 0,
        overdue: 0,
        ghostResolutions: 0,
      });
    }

    // Compter les tickets par jour
    tickets.forEach((ticket) => {
      const dateStr = ticket.created_at.split("T")[0];
      const stats = dailyStats.get(dateStr);
      if (stats) {
        stats.tickets++;
        if (ticket.status === "resolved") {
          stats.resolved++;
        }
        if (this.isTicketOverdue(ticket)) {
          stats.overdue++;
        }
      }
    });

    // Simuler les résolutions fantômes (à remplacer avec vraies données)
    tickets.forEach((ticket) => {
      const dateStr = ticket.created_at.split("T")[0];
      const stats = dailyStats.get(dateStr);
      if (stats && Math.random() < 0.2) {
        stats.ghostResolutions++;
      }
    });

    // Convertir en array
    return Array.from(dailyStats.entries()).map(([date, stats]) => ({
      date,
      tickets: stats.tickets,
      resolved: stats.resolved,
      overdue: stats.overdue,
      ghostResolutions: stats.ghostResolutions,
    }));
  }

  /**
   * Vérifie si un ticket est en retard
   */
  private isTicketOverdue(ticket: SupportTicket): boolean {
    if (ticket.status === "resolved" || ticket.status === "closed")
      return false;

    const slaDays = {
      blocker: 1,
      critical: 2,
      normal: 5,
      low: 10,
    };

    const slaDaysForSeverity =
      slaDays[ticket.severity as keyof typeof slaDays] || 5;
    const dueDate = new Date(ticket.created_at);
    dueDate.setDate(dueDate.getDate() + slaDaysForSeverity);

    return new Date() > dueDate;
  }

  /**
   * Calcule le Ticket Rate (tickets par 1000 utilisateurs actifs)
   */
  private async calculateTicketRate(
    tickets: SupportTicket[],
  ): Promise<{ ticketRate: number; totalUsers: number }> {
    try {
      // Simuler le nombre d'utilisateurs actifs (à remplacer avec vraie donnée)
      // Pour l'instant: 1 utilisateur actif tous les 10 tickets créés
      const totalUsers = Math.max(100, Math.floor(tickets.length / 10));

      // Calcul ticket rate: (tickets / utilisateurs) * 1000
      const ticketRate =
        totalUsers > 0 ? Math.round((tickets.length / totalUsers) * 1000) : 0;

      return { ticketRate, totalUsers };
    } catch (error) {
      logger.error("Error calculating ticket rate", "analytics", { error });
      // Valeurs par défaut en cas d'erreur
      return { ticketRate: 15, totalUsers: 100 };
    }
  }

  /**
   * Calcule les résolutions fantômes
   */
  private calculateGhostResolutions(tickets: SupportTicket[]): {
    ghostResolutions: number;
    ghostResolutionRate: number;
  } {
    // Simulé: 25% des tickets génèrent une résolution fantôme
    const ghostResolutions = Math.floor(tickets.length * 0.25);
    const ghostResolutionRate =
      tickets.length > 0 ? (ghostResolutions / tickets.length) * 100 : 0;

    return { ghostResolutions, ghostResolutionRate };
  }
}

export const supportAnalyticsService = SupportAnalyticsService.getInstance();
